import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@playerbase/ui';

import { supabase } from '../lib/supabase.js';
import { AuthLoading } from './protected-route.js';
import { redeemPendingInvite } from './invite-redemption.js';

// Auth credentials in a callback URL are single-use. React StrictMode in dev
// mounts effects twice, which would fire two redemption requests for the same
// credential - the second one fails or wins the race and breaks the post-
// exchange navigation. Track consumed credentials at module scope so the
// second mount is a no-op rather than a competing async flow.
const consumedCredentials = new Set<string>();

// Decode the email claim from a Supabase access-token JWT without establishing
// a session. Used to detect same- vs different-browser implicit-flow callbacks
// before deciding whether to actually sign the user in.
function decodeJwtEmail(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { email?: unknown };
    return typeof claims.email === 'string' ? claims.email : null;
  } catch {
    return null;
  }
}

export function AuthCallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const code = searchParams.get('code');
  const type = searchParams.get('type');
  const hasHashCallback = window.location.hash.length > 1;

  useEffect(() => {
    // Hash-fragment callback (implicit flow). Supabase uses this for resend
    // signup emails, magic links, and any token type that does not carry the
    // "pkce_" prefix - tokens land in the URL fragment instead of the query
    // string, so searchParams cannot see them.
    if (window.location.hash.length > 1) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorDescription = hashParams.get('error_description');
      if (errorDescription !== null) {
        setErrorMessage(errorDescription.replace(/\+/g, ' '));
        return;
      }

      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const hashType = hashParams.get('type');

      if (accessToken !== null && refreshToken !== null) {
        if (consumedCredentials.has(accessToken)) return;
        consumedCredentials.add(accessToken);

        // Recovery callbacks are always handled (the user explicitly requested
        // a password reset; they need a session to set the new password).
        if (hashType === 'recovery') {
          void (async () => {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) {
              setErrorMessage(error.message);
              return;
            }
            navigate('/reset-password', { replace: true });
          })();
          return;
        }

        // Sign-up confirmation via implicit flow (resend emails). Implicit
        // tokens work in any browser by design, but to match the PKCE flow's
        // same-browser-only UX we decode the JWT to read the verified email
        // *without* establishing a session, then compare to the marker that
        // sign-up wrote to localStorage. Only call setSession when the marker
        // matches; otherwise route to /sign-in with the existing flash. This
        // avoids the SIGNED_IN -> loadMemberships -> SIGNED_OUT race that
        // briefly leaves the auth context in an authenticated-no-membership
        // state that would otherwise redirect to /welcome.
        const verifiedEmail = decodeJwtEmail(accessToken);
        let signupOrigin: string | null = null;
        try {
          signupOrigin = localStorage.getItem('playerbase.auth.signup-origin');
        } catch {
          // localStorage may be unavailable
        }
        try {
          localStorage.removeItem('playerbase.auth.signup-origin');
        } catch {
          // localStorage may be unavailable
        }
        const sameBrowser = signupOrigin !== null && verifiedEmail !== null && signupOrigin === verifiedEmail;

        if (!sameBrowser) {
          navigate('/sign-in', {
            replace: true,
            state: { flash: 'email-verified' },
          });
          return;
        }

        void (async () => {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setErrorMessage(error.message);
            return;
          }
          const redemption = await redeemPendingInvite();
          if (redemption !== null) {
            navigate(`/s/${redemption.shortId}`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        })();
        return;
      }
    }

    // Query-string callback (PKCE flow).
    if (code === null) return;
    if (consumedCredentials.has(code)) return;
    consumedCredentials.add(code);

    let recoverySeen = false;

    // Supabase fires PASSWORD_RECOVERY during exchangeCodeForSession when the
    // code originated from a recovery email. This is the canonical signal -
    // more reliable than the ?type=recovery query hint.
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') recoverySeen = true;
    });

    void (async () => {
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      listener.subscription.unsubscribe();

      if (error) {
        // PKCE verifier missing = link opened in a different browser/device or
        // storage was cleared. The original email is already verified server-
        // side; we just cannot mint a session locally. Branch on the URL hint
        // to route the user somewhere actionable.
        if (/code verifier/i.test(error.message)) {
          if (type === 'recovery') {
            navigate('/forgot-password', {
              replace: true,
              state: { flash: 'reset-link-different-browser' },
            });
          } else {
            navigate('/sign-in', {
              replace: true,
              state: { flash: 'email-verified' },
            });
          }
          return;
        }
        setErrorMessage(error.message);
        return;
      }

      if (recoverySeen || type === 'recovery') {
        navigate('/reset-password', { replace: true });
        return;
      }

      try {
        localStorage.removeItem('playerbase.auth.signup-origin');
      } catch {
        // localStorage may be unavailable
      }

      // If the user signed in/up via an invite link, redeem the token here
      // before falling back to the standard "/" routing. New-via-invite users
      // get the personal studio created server-side and land directly in the
      // inviting studio, skipping /welcome entirely.
      const redemption = await redeemPendingInvite();
      if (redemption !== null) {
        navigate(`/s/${redemption.shortId}`, { replace: true });
        return;
      }

      // Sign-up confirmation, OAuth callback, magic link, etc - send the user
      // to the root so ProtectedRoute can route them to /welcome (no studio
      // yet) or the dashboard (existing studio).
      navigate('/', { replace: true });
    })();
  }, [code, type, navigate]);

  if (errorMessage !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4">
          <Alert tone="danger">
            {errorMessage}{' '}
            <a href="/sign-in" className="underline">
              Back to sign in
            </a>
          </Alert>
        </div>
      </div>
    );
  }

  if (code === null && !hasHashCallback) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4">
          <Alert tone="danger">
            Missing code. The link may be invalid or expired.{' '}
            <a href="/sign-in" className="underline">
              Back to sign in
            </a>
          </Alert>
        </div>
      </div>
    );
  }

  return <AuthLoading />;
}
